import { FormParamStatus } from "@/lib/form/types"
import {
  isParameterVisibleForMarket,
  V2Department,
  V2Machine,
  V2MarketType,
  V2ParameterItem,
  V2ParameterSection,
  V2Product,
  V2ProductsMap,
  V2SectionDisplayNames,
  V2SectionOrder,
  V2SKU,
} from "@/packaging-v2/lib/schema"
import {
  deepClone,
  generateId,
  generateTimeWindows,
  getParamErrorFlagState,
  getWindowErrorFlagState,
  parseShiftTimes,
} from "@/packaging-v2/lib/utils"
import { ErrorFlagState } from "@/components/ui/input"
import {
  V2FormSchema,
  V2ItemFormData,
  V2MachineFormData,
  V2MachineFormSchema,
  V2ParamFormData,
  V2SectionFormData,
} from "@/store/packaging-v2"

// ============================================
// Schema to Form Schema Transformation
// ============================================

/**
 * Convert a V2Department to a V2FormSchema
 * Creates initial structure with empty items for each machine
 */
export function v2DeptSchemaToFormSchema(
  deptSchema: V2Department
): V2FormSchema {
  return {
    id: deptSchema.id,
    name: deptSchema.name,
    machines: deptSchema.machines.map((machine) => ({
      id: machine.id,
      name: machine.name,
      deptId: deptSchema.id,
      items: [], // Start with no items - user will add them
    })),
  }
}

/**
 * Create a new empty item for a machine
 */
export function createEmptyItem(sequenceOrder: number): V2ItemFormData {
  return {
    id: generateId(),
    sequenceOrder,
    marketType: V2MarketType.DOMESTIC,
    productId: undefined,
    productName: undefined,
    skuId: undefined,
    skuName: undefined,

    sections: [], // Sections will be loaded when SKU is selected
    status: FormParamStatus.NEW,
  }
}

/**
 * Create initial V2MachineFormData from schema
 */
export function createMachineFormData(
  machine: V2Machine,
  deptId: string
): V2MachineFormData {
  return {
    id: machine.id,
    name: machine.name,
    deptId,
    items: [],
  }
}

// ============================================
// Form Schema Builder Class
// ============================================

export interface BuildSectionsOptions {
  shiftStartedAt: string
  shiftEndedAt: string
}

export class V2FormSchemaBuilder {
  private deptSchema: V2Department
  private formSchema: V2FormSchema
  private productsMap: V2ProductsMap

  constructor(
    deptSchema: V2Department,
    productsMap: V2ProductsMap,
    existingForm?: V2FormSchema
  ) {
    this.deptSchema = deptSchema
    this.productsMap = productsMap
    this.formSchema = existingForm || v2DeptSchemaToFormSchema(deptSchema)
  }

  /**
   * Get the current form schema
   */
  getFormSchema(): V2FormSchema {
    return this.formSchema
  }

  /**
   * Get machine schema by ID from department
   */
  getMachine(machineId: string): V2Machine | undefined {
    return this.deptSchema.machines.find((m) => m.id === machineId)
  }

  /**
   * Get product by ID from machine
   */
  getProduct(machineId: string, productId: string): V2Product | undefined {
    const machine = this.getMachine(machineId)
    if (!machine) return undefined
    return this.productsMap[productId]
  }

  /**
   * Get SKU by ID from product
   */
  getSKU(
    machineId: string,
    productId: string,
    skuId: string
  ): V2SKU | undefined {
    const product = this.getProduct(machineId, productId)
    return product?.skus.find((s) => s.id === skuId)
  }

  /**
   * Get machine form schema
   */
  getMachineFormSchema(machineId: string): V2MachineFormSchema | undefined {
    return this.formSchema.machines.find((m) => m.id === machineId)
  }

  /**
   * Add a new item to a machine
   */
  addItem(machineId: string): V2ItemFormData | undefined {
    const machineForm = this.getMachineFormSchema(machineId)
    if (!machineForm) {
      return undefined
    }

    const sequenceOrder = machineForm.items.length + 1
    const newItem = createEmptyItem(sequenceOrder)
    machineForm.items.push(newItem)

    return newItem
  }

  /**
   * Remove an item from a machine
   */
  removeItem(machineId: string, itemIndex: number): void {
    const machineForm = this.getMachineFormSchema(machineId)
    if (!machineForm || !machineForm.items[itemIndex]) {
      return
    }

    machineForm.items.splice(itemIndex, 1)

    // Re-sequence items
    machineForm.items.forEach((item, idx) => {
      item.sequenceOrder = idx + 1
    })
  }

  /**
   * Set the product and SKU for an item, and build all sections
   */
  setSKUForItem(
    machineId: string,
    itemIndex: number,
    productId: string,
    skuId: string,
    marketType: V2MarketType,
    options: BuildSectionsOptions
  ): void {
    const machineForm = this.getMachineFormSchema(machineId)
    if (!machineForm || !machineForm.items[itemIndex]) {
      return
    }

    const product = this.getProduct(machineId, productId)
    const sku = this.getSKU(machineId, productId, skuId)

    if (!product || !sku) {
      return
    }

    const item = machineForm.items[itemIndex]
    item.productId = productId
    item.productName = product.name
    item.skuId = skuId
    item.skuName = sku.name
    item.marketType = marketType

    // Build all sections from SKU parameters
    item.sections = this.buildAllSections(sku, marketType, options)
  }

  /**
   * Update market type for an item (rebuilds sections if SKU is set)
   */
  setMarketTypeForItem(
    machineId: string,
    itemIndex: number,
    marketType: V2MarketType,
    options: BuildSectionsOptions
  ): void {
    const machineForm = this.getMachineFormSchema(machineId)
    if (!machineForm || !machineForm.items[itemIndex]) {
      return
    }

    const item = machineForm.items[itemIndex]
    item.marketType = marketType

    // If SKU is already selected, rebuild sections with new market type
    if (item.productId && item.skuId) {
      const sku = this.getSKU(machineId, item.productId, item.skuId)
      if (sku) {
        // Preserve existing values where possible
        const existingValues = this.extractExistingValues(item)
        item.sections = this.buildAllSections(sku, marketType, options)
        this.restoreExistingValues(item, existingValues)
      }
    }
  }

  /**
   * Extract existing parameter values from an item
   */
  private extractExistingValues(
    item: V2ItemFormData
  ): Map<string, V2ParamFormData> {
    const values = new Map<string, V2ParamFormData>()
    for (const section of item.sections) {
      for (const param of section.parameters) {
        values.set(param.id, deepClone(param))
      }
    }
    return values
  }

  /**
   * Restore existing values to newly built sections
   */
  private restoreExistingValues(
    item: V2ItemFormData,
    existingValues: Map<string, V2ParamFormData>
  ): void {
    for (const section of item.sections) {
      for (const param of section.parameters) {
        const existing = existingValues.get(param.id)
        if (existing) {
          param.value = existing.value
          param.remark = existing.remark
          param.recordedAt = existing.recordedAt
          param.status = existing.status
          // Restore window values if applicable
          if (existing.windows && param.windows) {
            for (let i = 0; i < param.windows.length; i++) {
              if (existing.windows[i]) {
                param.windows[i].value = existing.windows[i].value
                param.windows[i].recordedAt = existing.windows[i].recordedAt
                param.windows[i].remark = existing.windows[i].remark
              }
            }
          }
        }
      }
    }
  }

  /**
   * Build all 5 sections from SKU parameters
   */
  buildAllSections(
    sku: V2SKU,
    marketType: V2MarketType,
    options: BuildSectionsOptions
  ): V2SectionFormData[] {
    const sections: V2SectionFormData[] = []

    for (const sectionType of V2SectionOrder) {
      const params = sku.parameters[sectionType] || []
      const section = this.buildSection(
        sectionType,
        params,
        marketType,
        options
      )
      sections.push(section)
    }

    return sections
  }

  /**
   * Build a single section from parameters
   */
  buildSection(
    sectionType: V2ParameterSection,
    params: V2ParameterItem[],
    marketType: V2MarketType,
    options: BuildSectionsOptions
  ): V2SectionFormData {
    const { shiftStart, shiftEnd } = parseShiftTimes(
      options.shiftStartedAt,
      options.shiftEndedAt
    )

    // Filter params by market type
    const visibleParams = params.filter((p) =>
      isParameterVisibleForMarket(p.marketType, marketType)
    )

    // Sort by sequence order
    visibleParams.sort((a, b) => a.sequenceOrder - b.sequenceOrder)

    const paramForms: V2ParamFormData[] = visibleParams.map((param) => {
      const paramForm: V2ParamFormData = {
        id: param.id,
        name: param.name,
        inputType: param.inputType,
        validationType: param.validationType,
        unit: param.unit || undefined,
        minLimit: param.minLimit ?? undefined,
        maxLimit: param.maxLimit ?? undefined,
        possibleValues:
          param.possibleValues.length > 0 ? param.possibleValues : undefined,
        correctValue: param.correctValue || undefined,
        frequencyMinutes: param.frequencyMinutes,
        marketType: param.marketType,
        required: param.required,
        value: undefined,
        remark: undefined,
        recordedAt: undefined,
        status: FormParamStatus.NEW,
      }

      // Generate windows for frequency-based parameters
      if (param.frequencyMinutes) {
        paramForm.windows = generateTimeWindows(
          param.frequencyMinutes,
          shiftStart,
          shiftEnd
        )
      }

      return paramForm
    })

    return {
      id: generateId(),
      type: sectionType,
      name: V2SectionDisplayNames[sectionType],
      isExpanded: true, // All sections expanded by default
      status: FormParamStatus.NEW,
      parameters: paramForms,
    }
  }

  /**
   * Update parameter value
   */
  setParamValue(
    machineId: string,
    itemIndex: number,
    sectionType: V2ParameterSection,
    paramId: string,
    value: string,
    recordedAt?: string
  ): void {
    const machineForm = this.getMachineFormSchema(machineId)
    if (!machineForm || !machineForm.items[itemIndex]) {
      return
    }

    const item = machineForm.items[itemIndex]
    const section = item.sections.find((s) => s.type === sectionType)
    if (!section) {
      return
    }

    const param = section.parameters.find((p) => p.id === paramId)
    if (!param) {
      return
    }

    param.value = value
    param.recordedAt = recordedAt || new Date().toISOString()
    param.status = FormParamStatus.DRAFT
  }

  /**
   * Update window value for a frequency-based parameter
   */
  setWindowValue(
    machineId: string,
    itemIndex: number,
    sectionType: V2ParameterSection,
    paramId: string,
    windowIndex: number,
    value: string,
    recordedAt?: string
  ): void {
    const machineForm = this.getMachineFormSchema(machineId)
    if (!machineForm || !machineForm.items[itemIndex]) {
      return
    }

    const item = machineForm.items[itemIndex]
    const section = item.sections.find((s) => s.type === sectionType)
    if (!section) {
      return
    }

    const param = section.parameters.find((p) => p.id === paramId)
    if (!param || !param.windows || !param.windows[windowIndex]) {
      return
    }

    param.windows[windowIndex].value = value
    param.windows[windowIndex].recordedAt =
      recordedAt || new Date().toISOString()
    param.status = FormParamStatus.DRAFT
  }

  /**
   * Mark a section as submitted
   */
  submitSection(
    machineId: string,
    itemIndex: number,
    sectionType: V2ParameterSection
  ): void {
    const machineForm = this.getMachineFormSchema(machineId)
    if (!machineForm || !machineForm.items[itemIndex]) {
      return
    }

    const item = machineForm.items[itemIndex]
    const section = item.sections.find((s) => s.type === sectionType)
    if (!section) {
      return
    }

    section.status = FormParamStatus.SUBMITTED

    // Mark all params as submitted
    for (const param of section.parameters) {
      if (param.value || (param.windows && param.windows.some((w) => w.value))) {
        param.status = FormParamStatus.SUBMITTED
      }
    }

    // Update item status
    this.updateItemStatus(machineId, itemIndex)
  }

  /**
   * Update item status based on section statuses
   */
  private updateItemStatus(machineId: string, itemIndex: number): void {
    const machineForm = this.getMachineFormSchema(machineId)
    if (!machineForm || !machineForm.items[itemIndex]) {
      return
    }

    const item = machineForm.items[itemIndex]

    // Check if all sections are submitted
    const allSubmitted = item.sections.every(
      (s) => s.status === FormParamStatus.SUBMITTED
    )

    if (allSubmitted) {
      item.status = FormParamStatus.SUBMITTED
    } else if (item.sections.some((s) => s.status === FormParamStatus.DRAFT)) {
      item.status = FormParamStatus.DRAFT
    }
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get products available for a machine
 */
export function getProductsForMachine(
  machine: V2Machine,
  productsMap: V2ProductsMap
): V2Product[] {
  return machine.products
    .map((id) => productsMap[id])
    .filter((p): p is V2Product => p !== undefined)
}

/**
 * Get SKUs available for a product
 */
export function getSKUsForProduct(
  productId: string,
  productsMap: V2ProductsMap
): V2SKU[] {
  const product = productsMap[productId]
  return product?.skus || []
}

/**
 * Check if an item has all required fields filled
 */
export function isItemComplete(item: V2ItemFormData): boolean {
  if (!item.productId || !item.skuId) {
    return false
  }

  // Check if at least one section has submitted params
  return item.sections.some((s) => s.status === FormParamStatus.SUBMITTED)
}

/**
 * Check if a section is complete (all required params have values and all error flags have remarks)
 */
export function isSectionComplete(section: V2SectionFormData): boolean {
  let totalLeaked = 0
  let breakdownParamId: string | undefined
  
  // Special handling for PRIMARY_PACKAGING section with leakage params
  if (section.type === V2ParameterSection.PRIMARY_PACKAGING) {
    const checkedParam = section.parameters.find(p => 
      p.name.toLowerCase().includes('check') && !p.name.toLowerCase().includes('leakage')
    )
    const leakedParam = section.parameters.find(p => 
      p.name.toLowerCase().includes('leaked')
    )
    const breakdownParam = section.parameters.find(p => 
      p.name.toLowerCase().includes('breakdown') || p.name.toLowerCase().includes('leakage type')
    )
    breakdownParamId = breakdownParam?.id
    
    // If leakage params exist, validate them specially
    if (checkedParam && leakedParam && breakdownParam) {
      // Both checked and leaked must have values
      if (!checkedParam?.value || checkedParam.value === '') {
        // Fall through to normal validation
      } else if (!leakedParam?.value || leakedParam.value === '') {
        // Fall through to normal validation
      } else {
        totalLeaked = Number(leakedParam.value) || 0
        
        // If there are leaked packets, breakdown must be valid
        if (totalLeaked > 0) {
          if (breakdownParam?.value) {
            try {
              const data = JSON.parse(breakdownParam.value)
              const entries = data.entries || []
              const entriesSum = entries.reduce((sum: number, entry: {count?: number}) => sum + (entry.count || 0), 0)
              
              // Check if sum of breakdown entries equals total leaked
              if (entriesSum !== totalLeaked) {
                return false
              }
              
              // Check if all entries have a type selected
              const hasEmptyType = entries.some((entry: {type?: string}) => !entry.type || entry.type === '')
              if (hasEmptyType) {
                return false
              }
              
              // Check breakdown has remark when there's leakage
              const hasRemark = !!(data.remark || breakdownParam.remark)
              if (!hasRemark) {
                return false
              }
            } catch {
              return false
            }
          } else {
            // totalLeaked > 0 but no breakdown value
            return false
          }
        }
      }
    }
  }
  
  for (const param of section.parameters) {
    if (
      section.type === V2ParameterSection.PRIMARY_PACKAGING &&
      breakdownParamId === param.id &&
      totalLeaked <= 0
    ) {
      continue
    }

    // Check required fields have values
    if (param.required) {
      if (param.frequencyMinutes && param.windows) {
        // For frequency params, check if at least one window has a value
        if (!param.windows.some((w) => w.value)) {
          return false
        }
      } else if (!param.value) {
        return false
      }
    }

    // Check if param has unremarked error flag (out-of-range or incorrect choice)
    if (param.value) {
      const flagState = getParamErrorFlagState(param)
      if (flagState === ErrorFlagState.Unfilled) {
        return false // Has error but no remark
      }
    }

    // Check windows for unremarked error flags
    if (param.windows) {
      for (const window of param.windows) {
        if (window.value) {
          const flagState = getWindowErrorFlagState(param, window)
          if (flagState === ErrorFlagState.Unfilled) {
            return false // Window has error but no remark
          }
        }
      }
    }
  }
  
  return true
}

/**
 * Check if a form (item) is complete - all sections are complete
 * Used to enable/disable the single "Confirm All" button
 */
export function isFormComplete(item: V2ItemFormData): boolean {
  if (!item.productId || !item.skuId) {
    return false
  }

  for (const section of item.sections) {
    // Skip INFORMATION_FIELDS as it's handled by ItemHeader
    if (section.type === V2ParameterSection.INFORMATION_FIELDS) {
      continue
    }
    if (!isSectionComplete(section)) {
      return false
    }
  }
  return true
}
